import { useState } from "react";
import { useLocation } from "wouter";

import {
  ApiBulkExport,
  BulkExportType,
  BulkFileConfig,
  CompositeMapConfig,
  RorConfig,
  useCreateBulkExport,
} from "@nens/ldo/api-v1";

import { useExportActionAllowed } from "../allowed-export-actions-hook";
import { isValidConfig } from "../domain";
import { getBulkExportListUrl } from "../routes";

export interface BulkExportNewHookProps {
  exportType: BulkExportType;
  name: string;
  description: string;
  bulkConfig: BulkFileConfig;
  compositeConfig: CompositeMapConfig;
  rorConfig: RorConfig;
  extra: Partial<ApiBulkExport>;
}

export function useBulkExportNew({
  exportType,
  name,
  description,
  bulkConfig,
  compositeConfig,
  rorConfig,
  extra,
}: BulkExportNewHookProps) {
  const [newExportConfigBulk, setNewExportConfigBulk] = useState<BulkFileConfig>(bulkConfig);
  const [newExportConfigComposite, setNewExportConfigComposite] =
    useState<CompositeMapConfig>(compositeConfig);
  const [newExportConfigRor, setNewExportConfigRor] = useState<RorConfig>(rorConfig);

  const [newExport, setNewExport] = useState({
    name,
    description,
  });

  const listUrl = getBulkExportListUrl(exportType);
  const actionAllowed = useExportActionAllowed(null);

  const setLocation = useLocation()[1];
  const successCallback = (bulkExport: ApiBulkExport) => setLocation(`${listUrl}/${bulkExport.id}`);

  const createExportMutation = useCreateBulkExport();

  const formErrorKeys: string[] = isValidConfig(
    exportType,
    newExport.name,
    exportType === "bulk_file"
      ? newExportConfigBulk
      : exportType === "composite_map"
      ? newExportConfigComposite
      : newExportConfigRor
  );

  const formValid = formErrorKeys.length === 0 && actionAllowed("create");

  const submit = () => {
    if (!formValid || !actionAllowed("create")) return;
    createExportMutation.mutate(
      {
        exportType,
        name: newExport.name,
        description: newExport.description,
        config:
          exportType === "bulk_file"
            ? newExportConfigBulk
            : exportType === "composite_map"
            ? newExportConfigComposite
            : newExportConfigRor,
        extra,
      },
      {
        onSuccess: successCallback,
      }
    );
  };

  return {
    setLocation,
    listUrl,
    newExport,
    setNewExport,
    newExportConfigBulk,
    setNewExportConfigBulk,
    newExportConfigComposite,
    setNewExportConfigComposite,
    newExportConfigRor,
    setNewExportConfigRor,
    formErrorKeys,
    formValid,
    formSubmitting: createExportMutation.isPending,
    submit,
  };
}
